import * as rimActions from './actions';
import * as sharedActions from '../../shared/actions';
import * as overviewActions from '../overview/actions';

import { DetailsRimActionTypes, DetailsRimState, RimLine, RimTab } from './types';
import { mapPolicyBusinessToRim, mapRimResponseToState, mapRimTotalsResponseToRim } from './mapper';
import { ActionType } from 'typesafe-actions';
import { BusinessResult } from '../../../business/models/policy-business.model';
import { Reducer } from 'redux';
import { RimResponse } from '../../../business/models/dto/rim/rim.response.dto';
import { RimViews } from './model';
import { SharedActionTypes } from '../../shared/types';
import { OverviewActionTypes } from '../overview/types';


const initialTabsState: RimTab[] = [
  { isDirty: false, isSaved: false },
  { isDirty: false, isSaved: false },
  { isDirty: false, isSaved: false },
  { isDirty: false, isSaved: false },
  { isDirty: false, isSaved: false },
  { isDirty: false, isSaved: false },
  { isDirty: false, isSaved: false }
];

const initialCurrentRimLine: RimLine = {
  id: 0,
  amount: '',
  amount80: '',
  description: '',
  invoicePresent: false,
  rimItemTC: '',
  rimSectionTC: '',
  sequence: 0,
  vatPercentageTC: '',
  vatThresholdTC: '',
  deleted: false,
  vatEnabled: false,
  vatPercentageEnabled: false,
  amountEnabled: false,
  invoicePresentEnabled: false,
  rimItemValuesBySection: [],
  isImportFromDacca: false
};

const initialState: DetailsRimState = {
  previousView: RimViews.valueAndCause,
  currentView: RimViews.valueAndCause,
  insuredAddress: '',
  insuredPersonName: '',
  rimsTotal: [],
  rimHeaderId: -1,
  valueAndCause: {
    valueBuildingBeforeAccident: '',
    valueContentBeforeAccident: '',
    damageCauseRemark: '',
    cloneInsuredAmountBuilding: '',
    cloneInsuredAmountContent: '',
    cloned: false,
  },
  vatAndPayment: {
    deductibleVatPercentage: '',
    vat6ToBeJustifiedAmount: 0,
    vat21ToBeJustifiedAmount: 0,
    paidByBankTransfer: false,
    bban: '',
    bicCode: '',
    iban: ''
  },
  counterExpertise: {
    nameTP: '',
    nameTPCompany: '',
    referteTP: '',
    realValue: '',
    counterExpertSignedName: '',
    comment: ''
  },
  remark: {
    remark: '',
    remarkSelected: false
  },
  communication: {
    sendEmailToPolicyHolder: false,
    emailPolicyHolder: '',
    sendEmailToCounterExpert: false,
    emailCounterExpert: '',
    sendEmailToPersonalExpert: false,
    emailPersonalExpert: '',
    sendEmailToOther: false,
    emailOther: '',
    sendDaccaEmailToPolicyHolder: false,
    sendDaccaEmailToCounterExpert: false,
    sendDaccaEmailToPersonalExpert: false,
    sendDaccaEmailToOther: false,
    sent: false
  },
  signing: {
    signatureCounterExpert: '',
    personalExpertSignedName: '',
    signaturePersonalExpert: '',
    signatureExpertOrInspector: '',
    signaturePolicyHolder: '',
    signedOn: '',
    cloneSuffererLocation: '',
    cloneSuffererName: '',
    reflectionTime: true
  },
  locked: false,
  rimLines: [],
  tabs: initialTabsState,
  rimTotalItemsTabs: {},
  rimLinesAmount80Total: 0,
  rimLinesAmountTotal: 0,
  rimTypeCodes: {
    vatPercentageValues: [],
    vatThresholdValues: [],
    rimItemValues: [],
    rimSectionValues: [],
    rimStatusList: []
  },
  currentRimLine: initialCurrentRimLine,
  validated: false,
  createdByName: '',
  createdByFirstName: '',
  createdByLastName: '',
  createdByCompany: '',
  createdByCompanyDibCode: '',
  approved: undefined,
  validations: []
};

export type RimAction = ActionType<typeof rimActions>;
export type SharedAction = ActionType<typeof sharedActions>;
export type OverviewAction = ActionType<typeof overviewActions>;

const reducer: Reducer<DetailsRimState, RimAction | SharedAction | OverviewAction> = (state = initialState, action): DetailsRimState => {
  switch (action.type) {
    case DetailsRimActionTypes.CHANGE_VIEW: {
      return { ...state, previousView: state.currentView, currentView: action.payload.view };
    }
    case DetailsRimActionTypes.FETCH_RIMS_TOTAL_SUCCESS: {
      return mapRimTotalsResponseToRim(state, action.payload);
    }
    case DetailsRimActionTypes.FETCH_RIMS_TOTAL_FAIL: {
      return state;
    }
    case DetailsRimActionTypes.FETCH_RIMS_TYPECODES_SUCCESS: {
      return { ...state, rimTypeCodes: { ...action.payload } };
    }
    case DetailsRimActionTypes.FETCH_RIMS_TYPECODES_FAIL: {
      return state;
    }
    case DetailsRimActionTypes.FETCH_RIM_SUCCESS: {
      return mapRimResponseToState(state, action.payload);
    }
    case DetailsRimActionTypes.FETCH_RIM_FAIL: {
      return state;
    }
    case DetailsRimActionTypes.RESET_STATE: {
      return initialState;
    }
    case DetailsRimActionTypes.UPDATE_VALUES: {
      return { ...state, ...action.payload };
    }
    case DetailsRimActionTypes.SET_CURRENT_TAB_AS_DIRTY: {
      const updatedTabs = state.tabs.map((tab, index) =>
        index === state.currentView ? { ...tab, isDirty: true } : tab
      );

      return { ...state, tabs: updatedTabs };
    }
    case DetailsRimActionTypes.UPDATE_SIGNING: {
      const updatedTabs = [...state.tabs];
      updatedTabs[state.currentView] = { ...updatedTabs[state.currentView], isDirty: true };

      return { ...state, signing: action.payload.signing, tabs: updatedTabs };
    }
    case DetailsRimActionTypes.UPDATE_CURRENT_RIM_LINE: {
      return {
        ...state,
        currentRimLine: {
          ...state.currentRimLine, ...action.payload
        }
      };
    }
    case DetailsRimActionTypes.ADD_RIM_LINE: {
      const updatedTabs = [...state.tabs];
      updatedTabs[state.currentView] = { ...updatedTabs[state.currentView], isDirty: true };
      return { ...state, tabs: updatedTabs };
    }
    case DetailsRimActionTypes.IMPORT_LINES_FROM_DACCA_SUCCESS: {
      const deletedRimLines = state.rimLines
        .filter(p => p.id !== 0)
        .map(p => ({ ...p, deleted: true }));

      const updatedTabs = state.tabs.map((tab, index) =>
        index === state.currentView ? { ...tab, isDirty: true } : tab
      );
      return { ...state, rimLines: [...deletedRimLines, ...action.payload.rimLines], tabs: updatedTabs };
    }
    case DetailsRimActionTypes.REMOVE_RIM_LINE: {
      const updatedTabs = [...state.tabs];
      updatedTabs[state.currentView] = { ...updatedTabs[state.currentView], isDirty: true };

      return { ...state, tabs: updatedTabs };
    }
    case DetailsRimActionTypes.SET_RIM_LINES: {
      const typeActionPayload = action.payload as RimResponse;
      return { ...state, rimLines: [...typeActionPayload.rimLines], };
    }
    case DetailsRimActionTypes.UPDATE_VAT_SUM: {
      return {
        ...state,
        vatAndPayment: {
          ...state.vatAndPayment,
          vat6ToBeJustifiedAmount: action.payload.vat6ToBeJustifiedAmount,
          vat21ToBeJustifiedAmount: action.payload.vat21ToBeJustifiedAmount
        }
      };
    }
    case DetailsRimActionTypes.SET_RIM_ID: {
      return { ...state, rimHeaderId: action.payload };
    }
    case DetailsRimActionTypes.SET_TAB_AS_SAVED: {
      const updatedTabs = state.tabs.map((tab, index) =>
        index === action.payload ? { ...tab, isDirty: true } : tab
      );

      return { ...state, tabs: updatedTabs };
    }
    case DetailsRimActionTypes.ADD_RIM: {
      return { ...initialState, rimHeaderId: 0 };
    }
    case SharedActionTypes.BUSINESS_UPDATE: {
      return mapPolicyBusinessToRim(state, action.payload as BusinessResult);
    }
    case DetailsRimActionTypes.RIM_LINE_SECTION_CHANGE: {
      return {
        ...state,
        currentRimLine: {
          ...state.currentRimLine,
          rimSectionTC: action.payload,
        }
      };
    }
    case DetailsRimActionTypes.RIM_LINE_ITEM_CHANGE: {
      return {
        ...state, currentRimLine: {
          ...state.currentRimLine,
          rimItemTC: action.payload,
        }
      };
    }
    case DetailsRimActionTypes.RIM_LINE_VAT_THRESHOLD_CHANGE: {
      return {
        ...state,
        currentRimLine: {
          ...state.currentRimLine,
          vatThresholdTC: action.payload
        }
      };
    }
    case DetailsRimActionTypes.RIM_LINE_VAT_PERCENTAGE_CHANGE: {
      return {
        ...state,
        currentRimLine: {
          ...state.currentRimLine,
          vatPercentageTC: action.payload
        }
      };
    }
    case DetailsRimActionTypes.RIM_LINE_AMOUNT_CHANGE: {
      return {
        ...state,
        currentRimLine: {
          ...state.currentRimLine,
          amount: action.payload
        }
      };
    }
    case DetailsRimActionTypes.RIM_LINE_DESCRIPTION_CHANGE: {
      return {
        ...state,
        currentRimLine: {
          ...state.currentRimLine,
          description: action.payload
        }
      };
    }
    case DetailsRimActionTypes.RIM_LINE_INVOICE_PRESENT_CHANGE: {
      return {
        ...state,
        currentRimLine: {
          ...state.currentRimLine,
          invoicePresent: action.payload
        }
      };
    }
    case DetailsRimActionTypes.SET_RIM_LOCKED: {
      return {
        ...state,
        locked: true
      };
    }
    case DetailsRimActionTypes.SET_VALIDATED: {
      return {
        ...state,
        validated: action.payload
      };
    }
    case OverviewActionTypes.RIM_STATUS_CHANGE_SUCCESS: {
      const updatedRimList = state.rimsTotal.map(item =>
        item.rimHeaderId === action.payload.id
          ? { ...item, rimData: { ...item.rimData, rimStatusTC: action.payload.rimStatusTC } }
          : item
      );

      return { ...state, rimsTotal: updatedRimList };
    }
    case OverviewActionTypes.GENERATE_REPORTS_RIM_SUCCESS: {
      const updatedRimList = state.rimsTotal.map(rim => {
        const updated = action.payload.find(res => rim.rimData.rimHeaderId === res.rimHeaderId);
        return updated ? { ...rim, rimData: { ...rim.rimData, amazonKey: updated.amazonKey } } : rim;
      });
      
      return { ...state, rimsTotal: updatedRimList };
    }
    default:
      return state;
  }
};

export { reducer as detailsRimReducer, initialState as detailsRimInitialState };
